import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as reportsApi from '@/api/reports';
import type { ReportListParams, RejectReportInput } from '@/types/reports';

export function useReportsList(params?: ReportListParams) {
  return useQuery({
    queryKey: ['reports', params],
    queryFn: () => reportsApi.listReports(params),
    placeholderData: (prev) => prev,
  });
}

export function useReportDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['reports', 'detail', id],
    queryFn: () => reportsApi.getReportDetail(id as string),
    enabled: Boolean(id),
  });
}

export function usePendingReportsCount() {
  return useQuery({
    queryKey: ['reports', 'pending-count'],
    queryFn: reportsApi.getPendingCount,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useApproveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reportsApi.approveReport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useRejectReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RejectReportInput }) =>
      reportsApi.rejectReport(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}
